import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {ObjectKeys} from '@/utilities/helpers'
import {FormFieldBox} from '@/components/ui/form-atoms/FormAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {StyledPredefinedAdditionalText, StyledPredefinedAdditionalTextsFields} from './style'
import {additionalPredefinedTextMapToDescription, additionalPredefinedTextMapToLabel} from '../../utils'
import {MedicalReportFieldsSchema} from '../../formSchemas'

const PredefinedAdditionalTextsFieldsSchema = MedicalReportFieldsSchema.pick({
    additionalPredefinedText1: true,
    isAdditionalPredefinedText2: true
})
type PredefinedAdditionalTextsFieldsSchema = z.infer<typeof PredefinedAdditionalTextsFieldsSchema>

export const PredefinedAdditionalTextsFields = () => {
    const {t} = useTranslation()
    const form = useFormContext<PredefinedAdditionalTextsFieldsSchema>()
    const additionalPredefinedText1 = useWatch({control: form.control, name: 'additionalPredefinedText1'})
    const isAdditionalPredefinedText2 = useWatch({control: form.control, name: 'isAdditionalPredefinedText2'})

    return (
        <StyledPredefinedAdditionalTextsFields direction="column" gap={6}>
            <Controller
                control={form.control}
                name="additionalPredefinedText1"
                render={({field, fieldState}) => (
                    <Grid columns={3} columnGap={4} fullWidth>
                        {ObjectKeys(additionalPredefinedTextMapToLabel).map((predefinedTextKey, index) => (
                            <FormFieldBox
                                key={predefinedTextKey}
                                spanColumn={1}
                                hasError={!!fieldState.error?.message && fieldState.error.type != 'custom'}
                                isSelected={field.value == predefinedTextKey}
                            >
                                <Flexbox direction="column" gap={1}>
                                    <RadioField
                                        label={t(additionalPredefinedTextMapToLabel[predefinedTextKey], {
                                            number: index + 1
                                        })}
                                        checked={field.value === predefinedTextKey}
                                        name={field.name}
                                        value={predefinedTextKey}
                                        onChange={event => field.onChange(event.currentTarget.value)}
                                    />
                                </Flexbox>
                            </FormFieldBox>
                        ))}
                    </Grid>
                )}
            />

            {additionalPredefinedText1 && (
                <StyledPredefinedAdditionalText>
                    {t(additionalPredefinedTextMapToDescription[additionalPredefinedText1])}
                </StyledPredefinedAdditionalText>
            )}

            <Controller
                control={form.control}
                name="isAdditionalPredefinedText2"
                render={({field, fieldState}) => (
                    <Checkbox
                        id="isAdditionalPredefinedText2"
                        label="Testo aggiuntivo opzionale"
                        containerClass="reopening-checkbox-container"
                        disabled={fieldState.error?.message == 'custom'}
                        checked={field.value}
                        {...form.register('isAdditionalPredefinedText2')}
                    />
                )}
            />

            {isAdditionalPredefinedText2 && (
                <StyledPredefinedAdditionalText>
                    {t('medical_records:additional_predefined_text_4')}
                </StyledPredefinedAdditionalText>
            )}
        </StyledPredefinedAdditionalTextsFields>
    )
}
